import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { supabase } from '../lib/supabaseClient';
import { translateAuthError } from '../lib/authErrors';
import './Login.css';
import './ResetPassword.css';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

function ForgotPassword() {
  const location = useLocation();
  // Pré-remplit avec l'email éventuellement saisi sur l'écran de connexion.
  const [email, setEmail] = useState(location.state?.email ?? '');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [sent, setSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const turnstileRef = useRef(null);

  const isValid = email.includes('@') && !!captchaToken;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || loading) return;

    setErrorMsg(null);
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('reset-password-with-captcha', {
        body: {
          email,
          captchaToken,
          redirectTo: `${window.location.origin}/reset-password`,
        },
      });
      if (error) {
        const body = await error.context?.json?.().catch(() => null);
        setErrorMsg(translateAuthError(body?.code, body?.error ?? error.message));
      } else {
        setSent(true);
      }
    } catch {
      setErrorMsg('Erreur réseau. Vérifiez votre connexion.');
    } finally {
      // Un token Turnstile est à usage unique : on le régénère à chaque tentative.
      turnstileRef.current?.reset();
      setCaptchaToken(null);
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <div className="App-card">
        <h1 className="App-title">
          My<span>Travo</span>
        </h1>
        <p className="App-subtitle">Mot de passe oublié</p>

        {sent ? (
          // Message volontairement neutre : on ne révèle pas si le compte existe.
          <p className="Reset-success">
            Si un compte est associé à <strong>{email}</strong>, un lien de réinitialisation
            vient de vous être envoyé.
          </p>
        ) : (
          <form onSubmit={handleSubmit}>
            <p className="Reset-account">
              Saisissez votre email : nous vous enverrons un lien pour choisir un nouveau mot de passe.
            </p>

            <div className="Field">
              <label className="Field-label" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className="Field-input"
                placeholder="vous@exemple.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {TURNSTILE_SITE_KEY && (
              <div className="Field-turnstile">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={setCaptchaToken}
                  onExpire={() => setCaptchaToken(null)}
                  onError={() => setCaptchaToken(null)}
                />
              </div>
            )}

            {errorMsg && <p className="Login-error">{errorMsg}</p>}

            <button type="submit" className="App-submit" disabled={!isValid || loading}>
              {loading ? 'Envoi…' : 'Envoyer le lien'}
            </button>
          </form>
        )}

        <Link to="/login" className="Reset-link ForgotPassword-back">Retour à la connexion</Link>
      </div>
    </div>
  );
}

export default ForgotPassword;
